import React, { Component } from 'react'
import { connect } from 'react-redux'
import { setUploading, setWizardErrorMessage } from '../WizardActions'
import { UPLOAD_FILES } from '../../../requests'
import { getFreshValueFromStoreByFieldId } from 'utilities'
import CloudUpload from '@tecuity/sos-icons/build/react-icons/cloud-upload'
import shortid from 'shortid'
import { SUBROUTES } from '../../../App'

const MAX_FILE_UPLOAD_SIZE = process.env.MAX_FILE_UPLOAD_SIZE || 10000000
const UPLOAD_FILE_FORMAT_MERGEABLE_ERROR = process.env.UPLOAD_FILE_FORMAT_MERGEABLE_ERROR
const UPLOAD_FILE_SIZE_MERGEABLE_ERROR = process.env.UPLOAD_FILE_SIZE_MERGEABLE_ERROR

class FileUpload extends Component {
  constructor(props) {
    super()
    this.state = {
      progress: 0,
      droppable: false,
      fileSizeError: '',
    }
  }
  preventDrop = (e) => {
    e.preventDefault()
  }
  hideDroppableIndicator = (e) => {
    this.preventDrop(e)
    this.setState({ droppable: false })
  }
  showDroppableIndicator = (e) => {
    this.preventDrop(e)
    this.setState({ droppable: true })
  }
  componentDidMount = () => {
    window.addEventListener('dragover', this.showDroppableIndicator, false)
    window.addEventListener('drop', this.hideDroppableIndicator, false)
  }
  componentWillUnmount = () => {
    window.removeEventListener('dragover', this.showDroppableIndicator, false)
    window.removeEventListener('drop', this.hideDroppableIndicator, false)
  }
  componentDidUpdate = (prevProps) => {
    let filesWereAddedToQueue = false
    let wasWaitingForOtherQueue = false
    const renderedByAccumulator = this.props.parent.type === 'UploadAccumulator'
    if (!renderedByAccumulator) {
      const currentPending = this.getNumByStatus(this.props.value, 'pending')
      filesWereAddedToQueue = currentPending > this.getNumByStatus(prevProps.value, 'pending')
      wasWaitingForOtherQueue = currentPending > 0 && !this.props.uploading && prevProps.uploading
    }
    if (
      (filesWereAddedToQueue && !renderedByAccumulator && !this.props.uploading) ||
      wasWaitingForOtherQueue
    ) {
      this.startQueue()
    }
  }
  startQueue = () => {
    this.props.setUploading(true)
    const limitedQueue = this.getFreshQueue()
      .filter((fileObj) => fileObj.status === 'pending')
      .slice(0, 5)
    limitedQueue.forEach((fileObj) => {
      this.props.onChange(this.getNewFileObjFromPayload(fileObj.queueId, { status: 'uploading' }))
    })
    limitedQueue.forEach((fileObj) => {
      this.startFileUpload(fileObj)
    })
  }
  startFileUpload = (fileObj) => {
    this.props.onChange(this.getNewFileObjFromPayload(fileObj.queueId, { status: 'uploading' }))
    uploadFile(
      fileObj.file,
      {
        SORT_ORDER: fileObj.sortOrder || 0,
        IMAGE_DATA_TYPE_ID: fileObj.imageTypeId,
        SourceTypeId: SUBROUTES.find((x) => x.parameter == this.props.category).SourceTypeId,
      },
      (e) => {
        if (e.lengthComputable) {
          let progress = (e.loaded / e.total) * 100
          this.props.onChange(this.getNewFileObjFromPayload(fileObj.queueId, { progress }))
        }
      },
      fileObj,
    )
      .then((guid) => {
        // console.log("%c" + fileObj.file.name, 'background: #52c848; padding: 2px; border-radius: 3px;')
        this.props.onChange(
          this.getNewFileObjFromPayload(fileObj.queueId, { status: 'success', guid }),
        )
        this.checkForAndRunNextItemInQueue()
      })
      .catch((err) => {
        console.error(err)
        this.setState({ fileSizeError: err })
        this.props.onChange(
          this.getNewFileObjFromPayload(fileObj.queueId, {
            status: 'failed',
            rejectReason: err.displayInWizard ? err.message : null,
          }),
        )
        this.checkForAndRunNextItemInQueue()
      })
  }
  checkForAndRunNextItemInQueue = () => {
    const numPending = this.getNumByStatus(this.getFreshQueue(), 'pending')
    if (numPending > 0) {
      this.startFileUpload(this.getNextItemInQueue())
    } else {
      this.props.setUploading(false)
    }
  }
  getNextItemInQueue = () => {
    const queue = this.getFreshQueue()
    return queue.filter((fileObj) => fileObj.status === 'pending')[0]
  }
  getFreshQueue = () => {
    const fromEntityNameSearch = this.props.parent && this.props.parent.type === 'EntityNameSearch'
    return getFreshValueFromStoreByFieldId(
      fromEntityNameSearch ? this.props.parent.id : this.props.fieldId,
      fromEntityNameSearch,
    )
  }
  getNumByStatus = (queue, status) => {
    return (queue || []).reduce((num, file) => (file.status === status ? num + 1 : num), 0)
  }
  getNewFileObjFromPayload = (queueId, payload) => {
    const value = this.getFreshQueue()
    const index = value.findIndex((file) => file.queueId === queueId)
    return [...value.slice(0, index), { ...value[index], ...payload }, ...value.slice(index + 1)]
  }
  handleChange = (e) => {
    let milliseconds = Date.now()
    let allowedExtensions = this.props.accept
      .trim()
      .split(',')
      .map((ext) => ext.trim().toLowerCase().replace(/\./g, ''))
    let files = [...e.target.files].map((file, i) => {
      const extension = file.name.split('.').pop().toLowerCase()
      let rejectImmediately = false
      let rejectReason = ''
      if (file.size > MAX_FILE_UPLOAD_SIZE) {
        rejectImmediately = true
        rejectReason = UPLOAD_FILE_SIZE_MERGEABLE_ERROR
          ? UPLOAD_FILE_SIZE_MERGEABLE_ERROR.replace(
              '{{FILE_SIZE}}',
              MAX_FILE_UPLOAD_SIZE / 1000000,
            )
          : 'Exceeded max file size'
      }
      if (this.props.accept) {
        if (allowedExtensions.every((ext) => ext !== extension)) {
          rejectImmediately = true
          rejectReason = UPLOAD_FILE_FORMAT_MERGEABLE_ERROR
            ? UPLOAD_FILE_FORMAT_MERGEABLE_ERROR.replace(
                '{{FILE_FORMAT}}',
                allowedExtensions.join(', ').toUpperCase(),
              )
            : 'Unsupported file format.'
        }
      }
      return {
        file,
        queuePosition: milliseconds + i,
        guid: null,
        rejectImmediately,
        rejectReason,
        status: 'pending',
        progress: 0,
        fieldId: this.props.fieldId,
        queueId: shortid.generate(),
        sortOrder: this.props.sortOrder,
        imageTypeId: this.props.imageTypeId || 33,
        filename: file.name,
        '@type': 'file',
        type: file.type,
      }
    })
    this.upload.value = null
    this.props.onChange([...(Array.isArray(this.props.value) ? this.props.value : []), ...files], e)
  }
  updateProgress = (e) => {
    if (e.lengthComputable) {
      var percentComplete = e.loaded / e.total
      this.setState({
        progress: percentComplete * 100,
      })
    }
  }
  removeFileByIndex = (index, e) => {
    this.setState({ fileSizeError: '' })
    let files = [...this.props.value.slice(0, index), ...this.props.value.slice(index + 1)]
    this.props.onChange(files, { ...e, target: { ...e.target, name: this.props.fieldId } })
  }
  handleFileDrop = (e) => {
    e.preventDefault()
    let files = []
    for (let i = 0; i < e.dataTransfer.items.length; i++) {
      if (e.dataTransfer.items[i].kind === 'file') {
        const file = e.dataTransfer.items[i].getAsFile()
        if (file) files.push(file)
      }
    }
    if (files.length) this.handleChange({ target: { files, name: this.props.fieldId } })
  }
  render() {
    const { disabled, accept, fieldId, multiple, value = [], ariaLabelledby } = this.props
    return (
      <div className="file-wrapper">
        <div className="row">
          <div className="col-xl-7 col-lg-8 col-md-9 file-column">
            {value &&
              value.map((file, i) => (
                <File
                  {...file}
                  onRemoveFile={this.removeFileByIndex}
                  filename={file.filename}
                  index={i}
                  key={file.queueId}
                  disabled={disabled}
                />
              ))}
          </div>
        </div>
        {(multiple || value.length === 0) && (
          <div className="row">
            <div className="col-xl-7 col-lg-8 col-md-9">
              <input
                className="hidden-upload"
                type="file"
                id={fieldId}
                name={fieldId}
                disabled={disabled}
                onChange={this.handleChange}
                accept={accept}
                multiple={multiple}
                ref={(ref) => (this.upload = ref)}
                onBlur={this.props.onBlur}
                aria-labelledby={ariaLabelledby || `File Upload: ${this.props.label}`}
              />
              <label
                className={`styled-file-upload ${disabled ? 'disabled' : ''}`}
                htmlFor={fieldId}
                onDrop={this.handleFileDrop}
              >
                <span
                  className={`icon icon-upload ${
                    this.state.droppable ? 'droppable-animation' : ''
                  }`}
                />
                {this.state.droppable
                  ? 'Drop files here to upload'
                  : value.length > 0
                    ? 'Select additional files to upload'
                    : `Select ${multiple ? 'files' : 'a file'} to upload ${
                        accept ? `(${accept})` : ''
                      }`}
              </label>
            </div>
          </div>
        )}
        {this.state.fileSizeError && (
          <span className="field-error">{this.state.fileSizeError}</span>
        )}
      </div>
    )
  }
}
export default connect(
  (state) => ({
    wizard: state.wizard.wizard,
    uploading: state.wizard.uploading,
    category: state.wizard.wizard.category || state.designer.wizard.category,
  }),
  { setUploading, setWizardErrorMessage },
)(FileUpload)

class File extends Component {
  removeFile = (e) => {
    this.props.onRemoveFile(this.props.index, e)
  }
  render() {
    const { progress, filename, status, disabled, rejectReason } = this.props
    const success = status === 'success'
    const failed = status === 'failed'
    const uploading = status === 'uploading'
    return (
      <div className={`progress ${success ? 'success' : ''} ${failed ? 'failed' : ''}`}>
        <div className="file-icon">
          {uploading && <div className="shine-spinner" />}
          <CloudUpload className={`icon ${uploading ? 'spin-out' : 'spin-in'}`} />
        </div>
        <span className="filename">{filename}</span>
        {failed && <span className="failed-text">{rejectReason || 'Upload Failed'}</span>}
        {!disabled
          ? (success || failed) && (
              <button
                title={`Remove Upload: ${filename}`}
                aria-label={`Remove Upload: ${filename}`}
                className="remove"
                onClick={this.removeFile}
              >
                <span className="icon icon-close" />
              </button>
            )
          : null}
        <div className="bar" style={{ width: progress + '%' }} />
      </div>
    )
  }
}

//Defines an upload exception
function Exception(message) {
  this.message = message
  this.name = 'Upload Exception'
}

//Starts a new XHR upload request and reports back progress to the onProgress function.
export const uploadFile = (file, metadata, onProgress, fileObj) => {
  return new Promise((res, rej) => {
    if (fileObj.rejectImmediately) {
      rej(fileObj.rejectReason)
    } else {
      UPLOAD_FILES([file], metadata, onProgress)
        .then((guid) => {
          guid = JSON.parse(guid)
          //console.log(guid)
          if (typeof guid === 'string' && guid.length < 100 && guid !== 'false') {
            res(guid)
          } else {
            rej(guid)
            throw new Exception(
              'The returned guid was not a string. It likely returned an error object.',
            )
          }
        })
        .catch((err) => {
          rej(err)
        })
    }
  })
}
